import * as v from 'valibot'
import type { AccountRole, AccountStatus } from './accounts'
import type { Environment } from './api'

/**
 * Integrations — how the platform is used by anything that is not a person
 * in a browser: scripts, automations, model-driven tools. The design is one
 * passport: a **service token**, carried as `Authorization: Bearer wf_…`,
 * acting as its creator's account with a coarse scope ceiling. The token
 * opens no door the API has not already built — it only names how far the
 * holder may walk.
 *
 * Scopes are cumulative, widest last:
 *
 *   read    every working surface, read-only
 *   write   read, plus the verbs that shape the work
 *   admin   write, plus the administration surfaces
 *
 * Tokens never sign in, never manage themselves (their management endpoints
 * refuse a Bearer caller — only a live administrator session may mint or
 * revoke), and die with their creator's account status.
 */

export const API_TOKEN_SCOPES = ['read', 'write', 'admin'] as const
export type ApiTokenScope = (typeof API_TOKEN_SCOPES)[number]

/** How far a token may walk — wider scopes satisfy narrower demands. */
export const SCOPE_LEVEL: Record<ApiTokenScope, number> = { read: 1, write: 2, admin: 3 }

export function scopeLevel(scopes: readonly ApiTokenScope[]): number {
  return scopes.reduce((level, scope) => Math.max(level, SCOPE_LEVEL[scope]), 0)
}

export interface ApiToken {
  id: string
  name: string
  /** The secret is never stored; this fragment is all a listing may show. */
  prefix: string
  scopes: ApiTokenScope[]
  createdBy: string
  createdByName: string | null
  createdAt: string
  lastUsedAt: string | null
  revokedAt: string | null
  /** When the key stops working on its own — null means it lives until revoked. */
  expiresAt: string | null
}

/** The one answer that ever carries the secret — the moment of creation. */
export interface ApiTokenCreated extends ApiToken {
  token: string
}

const required = (max: number) => v.pipe(v.string(), v.trim(), v.minLength(1, 'This field is required.'), v.maxLength(max, `Use at most ${max} characters.`))

export const createApiTokenSchema = v.pipe(
  v.object({
    name: required(80),
    scopes: v.pipe(
      v.array(v.picklist(API_TOKEN_SCOPES)),
      v.minLength(1, 'Pick at least one scope.'),
      v.maxLength(API_TOKEN_SCOPES.length),
    ),
    /** Lifespan in whole days; absent, the key lives until it is revoked. */
    expiresInDays: v.optional(v.pipe(v.number(), v.integer('Use whole days.'), v.minValue(1, 'A key needs at least one day.'), v.maxValue(3650, 'Ten years is the longest a key may live.'))),
  }),
  v.check((input) => new Set(input.scopes).size === input.scopes.length, 'The scopes repeat.'),
)

export type CreateApiTokenInput = v.InferOutput<typeof createApiTokenSchema>

/**
 * What `GET /whoami` answers to the caller's own passport: a machine key sees
 * the key itself — what it is called, what it may reach, when it dies — and
 * both passports see the account they act as. Introspection is how an
 * integration verifies its credentials without touching any work data.
 */
export interface Whoami {
  kind: 'token' | 'session'
  account: {
    id: string
    name: string | null
    email: string
    role: AccountRole
    status: AccountStatus
  }
  token?: {
    id: string
    name: string
    scopes: ApiTokenScope[]
    createdAt: string
    expiresAt: string | null
    lastUsedAt: string | null
  }
}

/**
 * What `GET` on the base URL answers to an authenticated caller: every route
 * the API serves, with its auth class and — for a machine key — the narrowest
 * scope that reaches it. The manifest exists so an integration can discover
 * the surface by asking, not by guessing; it is never served without a
 * passport, because an unauthenticated enumeration of doors is a gift to an
 * attacker.
 */
export interface ApiManifestRoute {
  method: string
  path: string
  /** Who may call it: `public` before sign-in, `onboarding` before approval, `account` any approved member, `staff` the working surfaces, `admin` administrators, `runtime` an enrolled machine. */
  auth: 'public' | 'onboarding' | 'account' | 'staff' | 'admin' | 'runtime'
  /** Narrowest scope a token needs, `refused` where a token may never walk, null where only a browser session belongs. */
  tokenScope: ApiTokenScope | 'refused' | null
}

export interface ApiManifest {
  name: string
  version: string
  environment: Environment
  baseUrl: string
  routes: ApiManifestRoute[]
}
